import * as Axios from "axios";
import { AxiosInstance, AxiosResponse } from "axios";
import {
  convert,
  FilesResources,
  FilesResourcesV2,
  HttpMethod,
  ProcManagementResources,
  Resources,
  ResourcesV2,
  TtzkPortalResources,
  TypedCloudGovResource,
  TypedCloudGovResourceV2,
} from "./base";
import {
  BaseProcedureEntity,
  BaseProcedureType,
  ProcedureDetailType,
  ProcedureEntity,
  ProcedureType,
  PublishSetting,
} from "../domain/procedures/procedure-entity";
import { ProcedurePresentationType } from "../domain/procedures/procedure-presentation-entity";
import { MailTemplate } from "../domain/procedures/default-mail-cfg-entity";
import { ProcedureMailCfgType } from "../domain/procedures/procedure-mail-cfg-entity";
import { ProcedureQuotaEntity } from "../domain/procedures/procedure-quota-entity";
import {
  ApplicationEntity,
  ApplicationType,
  ApplyStatus,
  ModifiedApplicationContentType,
  ModifyInfoType,
  UploadIssueFile,
} from "../domain/application/application-entity";
import {
  ApplicantSchemaKeysMigrator,
  CustomerDlConfig,
  FormAccessRestriction,
  InvoiceItemSetting,
  LgpkiVerifyResult,
  PaymentType,
  PublishStatus,
  Template,
} from "@graffer-inc/cloud-gov-core";
import { FormData as GrafferFormData } from "@graffer-inc/form-schema";
import { MyNumberWithRawValueType } from "../domain/application/mynumber-with-raw-value";
import * as procManagement from "../domain/proc-management";
import * as ttzkPortal from "../domain/ttzk-portal";
import {
  Notice,
  PortalConfig,
  PortalConfigRequest,
  Shortcut,
  ShortcutDetail,
} from "../domain/ttzk-portal";
import { CalcRequestType } from "../domain/payment/calc-request";
import { ProcedureCreate } from "../domain/procedures/procedure-create";
import { InternalCommentType } from "../domain/application/internal-comment";
import { RefundRequestAPI } from "../domain/payment/refund-request";
import { PaymentOperationJSON } from "../domain/payment/payment-operation";
import { PaymentOperationFactory } from "../domain/payment/payment-operation-factory";
import { StatusUpdateErrorCodeLiterals } from "../service/smart-apply/applications/error-messages";
import { ApplicationTaskBulkOperationResponse } from "../domain/application/application-task";
import {
  ProcedureImportStatus,
  ProcedureImportStatusType,
  ProcedureImportStatusWithDestManageGroupId,
  ProcedureImportStatusWithDestManageGroupIdType,
} from "../domain/procedures/procedure-import-status";
import {
  CustomerReviewType,
  CustomerReviewWithProcName,
} from "../domain/customer-review/customer-review";
import { CSVEncoding } from "../domain/file-encoding";
import { NoticeRequest } from "../../components/pages/ttzk-portal/notices-create";
import { OrganizationEntity } from "../domain/organization/organization";
import { ApplicationRemovalTask } from "../domain/application/application-removal-task/application-removal-task";
import { CreateTaskResult } from "../domain/application/application-removal-task/create-task-result";
import { ApplicationRemovalTaskListItem } from "../domain/application/application-removal-task/application-removal-task-list";
import { ApplicationRemovalTaskStatus } from "../domain/application/application-removal-task/application-removal-task-status";
import { ProcedureAssignmentSettingRuleSet } from "../domain/procedure-assignment-setting-ruleset/procedure-assignment-setting-ruleset";
import { ProcedureAssignmentSettingRuleSetEntity } from "../domain/procedure-assignment-setting-ruleset/procedure-assignment-setting-ruleset-entity";
import { AssignmentSettingRuleSetInput } from "../domain/procedure-assignment-setting-ruleset/procedure-assignment-setting-ruleset";
import {
  ProductDataRemovalTaskCreateRequest,
  ProductDataRemovalTaskResponse,
  TargetTypeApiPathParamMap,
} from "../domain/product-data-removal-task";

export class CloudGovClient {
  http: AxiosInstance;

  constructor(axios: AxiosInstance) {
    this.http = axios;
  }

  fetchApplication = (id: string) =>
    TypedCloudGovResource<ApplicationType, any>(this)(
      `/applications/${id}`,
      HttpMethod.GET,
    ).then(convert(ApplicationEntity.fromJSON));
  fetchApplicationPaymentOperations = (id: string, paymentIndex: number) =>
    TypedCloudGovResource<PaymentOperationJSON[], object>(this)(
      `/applications/${id}/payment-operations/${paymentIndex.toString()}`,
      HttpMethod.GET,
    ).then(convert(PaymentOperationFactory.toEntityArray));
  addPaymentToApplication = (
    applicationId: string,
    calcReq: CalcRequestType & { version: number },
  ) =>
    TypedCloudGovResource<ApplicationType, any>(this)(
      `/applications/${applicationId}/payments`,
      HttpMethod.PUT,
      calcReq,
    ).then(convert(ApplicationEntity.fromJSON));

  withdrawnPostPayment = (
    applicationId: string,
    req: { paymentIndex: number; version: number },
  ) =>
    TypedCloudGovResource<ApplicationType, any>(this)(
      `/applications/${applicationId}/withdraw-post-payment`,
      HttpMethod.PUT,
      req,
    ).then(convert(ApplicationEntity.fromJSON));

  refundPaymentsV2 = (applicationId: string, req: RefundRequestAPI) =>
    TypedCloudGovResourceV2<ApplicationType, any>(this)(
      `/applications/${applicationId}/refund-payments`,
      HttpMethod.POST,
      req,
    ).then(convert(ApplicationEntity.fromJSON));

  fetchApplicationMyNumber = (
    id: string,
    refKey: string,
    applicationHistoryId?: string,
  ) =>
    TypedCloudGovResource<MyNumberWithRawValueType, any>(this)(
      `/applications/${id}/my-numbers?refKey=${refKey}${
        applicationHistoryId
          ? "&applicationHistoryId=" + applicationHistoryId
          : ""
      }`,
      HttpMethod.GET,
    );
  modifyApplication = (
    id: string,
    modifyContent: ModifiedApplicationContentType &
      ModifyInfoType & {
        version: number;
      },
  ) =>
    Resources(this)(`/applications/${id}/modify`, HttpMethod.PUT, {
      ...modifyContent,
    }).then(convert(ApplicationEntity.fromJSON));
  outputPDF = (id: string) =>
    FilesResources(this)(`/applications/${id}/output-pdf`, HttpMethod.GET, {
      responseType: "blob",
      dataType: "binary",
    });
  outputSamplePDF = (
    id: string,
    formData: GrafferFormData,
    schemaType: "Application" | "Meta" | "TenantProcessResult",
  ) =>
    FilesResourcesV2(this)(
      `/procedures/${id}/output-sample-pdf`,
      HttpMethod.POST,
      {
        jsonDocument: formData,
        schemaType,
      },
      {
        responseType: "blob",
        dataType: "binary",
      },
    );
  outputPDFForCustomer = (applicationId: string) =>
    FilesResources(this)(
      `/applications/${applicationId}/data-mapped-pdf`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
      },
    );
  updateApplicationStatus = (
    id: string,
    appl: {
      status: ApplyStatus;
      skipNotification: boolean;
      version: number;
      message?: string;
    },
  ) =>
    TypedCloudGovResource<ApplicationType, any>(this)(
      `/applications/${id}/status`,
      HttpMethod.PUT,
      appl,
    ).then(convert(ApplicationEntity.fromJSON));
  batchUpdateApplicationStatus = (request: {
    procedureId: string;
    ids?: string[];
    csv?: string;
    status: ApplyStatus;
    skipNotification: boolean;
  }) =>
    TypedCloudGovResource<
      Array<{ id: string; errorCode?: StatusUpdateErrorCodeLiterals }>,
      any
    >(this)(`/applications-batch-update-status`, HttpMethod.PUT, request);

  getRemovalApplicationTask = (id: string) =>
    TypedCloudGovResource<ApplicationRemovalTask, any>(this)(
      `/application-removal-tasks/${id}`,
      HttpMethod.GET,
      {},
    ).then(convert(ApplicationRemovalTask.fromJSON));
  registerRemovalApplicationTask = (request: {
    reason: string;
    scheduledRemoveDate: Date;
    csv: string;
    isDryRun: boolean;
  }) =>
    TypedCloudGovResource<CreateTaskResult, any>(this)(
      `/application-removal-tasks`,
      HttpMethod.POST,
      request,
    ).then(convert(CreateTaskResult.fromJSON));
  listRemovalApplicationTasks = (
    page: number,
    limit: number,
    statuses: ApplicationRemovalTaskStatus[],
    groupIDs: string[],
    procedureID?: string,
  ) =>
    TypedCloudGovResource<ApplicationRemovalTaskListItem[], any>(this)(
      `/application-removal-tasks?page=${page}&limit=${limit}`,
      HttpMethod.GET,
      {
        params: {
          statuses: statuses,
          procedureId: procedureID,
          groupIds: groupIDs,
        },
      },
    ).then(convert(ApplicationRemovalTaskListItem.fromJSONArray));
  cancelRemovalApplicationTask = (id: string) =>
    TypedCloudGovResource<ApplicationRemovalTask, any>(this)(
      `/application-removal-tasks/${id}/cancel`,
      HttpMethod.PUT,
      {},
    );

  bulkIssuePdfFile = (request: {
    procedureId: string;
    ids?: Array<string>;
    csv?: string;
    fileName: string;
    skipNotification: boolean;
  }) =>
    TypedCloudGovResource<ApplicationTaskBulkOperationResponse, any>(this)(
      `/application-tasks/bulk-issue-pdf-file`,
      HttpMethod.POST,
      request,
    );
  bulkUpdateTenantProcessResult = (request: {
    procedureId: string;
    csv: string;
  }) =>
    TypedCloudGovResource<ApplicationTaskBulkOperationResponse, any>(this)(
      `/application-tasks/bulk-update-tenant-process-result`,
      HttpMethod.POST,
      request,
    );
  addInternalComment = (id: string, appl: { content: any }) =>
    TypedCloudGovResource<InternalCommentType[] | null, any>(this)(
      `/applications/${id}/internal-comment`,
      HttpMethod.PUT,
      appl,
    );

  addIssueFile = (
    id: string,
    appl: { file: UploadIssueFile; version: number },
    skipNotification: boolean,
  ) =>
    Resources(this)(
      `/applications/${id}/issue-file?skipNotification=${skipNotification}`,
      HttpMethod.PUT,
      appl,
    ).then(convert(ApplicationEntity.fromJSON));

  deleteIssueFile = ({
    id,
    index,
    version,
  }: {
    id: string;
    index: number;
    version: number;
  }) =>
    Resources(this)(
      `/applications/${id}/issue-file/${index}?version=${version}`,
      HttpMethod.DELETE,
      {},
    ).then(convert(ApplicationEntity.fromJSON));

  verifyLgpkiFile = (aplId: string, fileIndex: number) =>
    TypedCloudGovResource<LgpkiVerifyResult, any>(this)(
      `/lgpki/verify-pdf`,
      HttpMethod.POST,
      {
        applicationId: aplId,
        fileIndex: fileIndex,
      },
    );

  // payments
  calcPayment = (req: CalcRequestType) =>
    TypedCloudGovResource<any, CalcRequestType>(this)(
      `/payment/calculate`,
      HttpMethod.POST,
      req,
    );

  // procedures
  fetchProcedures = (
    page: number,
    limit: number,
    filter?: {
      name?: string;
      publishStatus?: PublishStatus[];
    },
  ) =>
    TypedCloudGovResource<BaseProcedureType[], any>(this)(
      `/procedures?page=${page}&limit=${limit}`,
      HttpMethod.GET,
      {
        params: {
          ...filter,
        },
      },
    ).then(convert(BaseProcedureEntity.fromJSONArray));

  fetchArchivedProcedures = (
    page: number,
    limit: number,
    filter?: {
      name?: string;
    },
  ) =>
    TypedCloudGovResource<BaseProcedureType[], any>(this)(
      `/archived-procedures?page=${page}&limit=${limit}`,
      HttpMethod.GET,
      {
        params: {
          ...filter,
        },
      },
    ).then(convert(BaseProcedureEntity.fromJSONArray));

  fetchProcedure = (id: string, version?: number) =>
    TypedCloudGovResource<ProcedureType, object>(this)(
      `/procedures/${id}`,
      HttpMethod.GET,
      {
        params: version && {
          version,
        },
      },
    ).then(convert(ProcedureEntity.fromJSON));

  fetchLivingProcedure = (id: string) =>
    TypedCloudGovResource<ProcedureType, never>(this)(
      `/procedures/${id}/live`,
      HttpMethod.GET,
    ).then(convert(ProcedureEntity.fromJSON));

  // temporary function to create setting result; to be used only by tfd tenant
  downloadAssignSettingsXlsx = (orgId: string) =>
    Resources(this)(`/tmp-assignee-settings-xlsx/${orgId}`, HttpMethod.GET, {
      responseType: "blob",
      dataType: "binary",
    });

  fetchProcedureAssignmentSettingRuleSets = (page: number, limit: number) =>
    TypedCloudGovResource<ProcedureAssignmentSettingRuleSet[], object>(this)(
      `/procedure-assignment-setting-rulesets/tenants?page=${page}&limit=${limit}`,
      HttpMethod.GET,
    ).then(convert(ProcedureAssignmentSettingRuleSetEntity.fromJSONArray));

  createProcedureAssignmentSettingRuleSet = (
    request: AssignmentSettingRuleSetInput,
  ) =>
    TypedCloudGovResource<null, { input: AssignmentSettingRuleSetInput }>(this)(
      `/procedure-assignment-setting-rulesets`,
      HttpMethod.POST,
      { input: request },
    );

  updateProcedureAssignmentSettingRuleSet = (
    id: string,
    request: AssignmentSettingRuleSetInput,
  ) =>
    TypedCloudGovResource<null, { input: AssignmentSettingRuleSetInput }>(this)(
      `/procedure-assignment-setting-rulesets/${id}`,
      HttpMethod.PUT,
      { input: request },
    );

  getProcedureAssignmentSettingRuleSet = (id: string) =>
    TypedCloudGovResource<ProcedureAssignmentSettingRuleSet, object>(this)(
      `/procedure-assignment-setting-rulesets/${id}`,
      HttpMethod.GET,
    ).then(convert(ProcedureAssignmentSettingRuleSetEntity.fromJSON));

  archiveProcedureAssignmentSettingRuleSet = (id: string) =>
    TypedCloudGovResource<null, { id: string }>(this)(
      `/procedure-assignment-setting-rulesets/${id}/archive`,
      HttpMethod.PUT,
      { id },
    );

  fetchProceduresByOrgId = (
    orgId: string,
    page: number,
    limit: number,
    filter?: {
      name?: string;
      publishStatus?: PublishStatus[];
      groupIds?: string[];
    },
  ) =>
    TypedCloudGovResource<BaseProcedureType[], any>(this)(
      `/procedures/${orgId}/organizations?page=${page}&limit=${limit}`,
      HttpMethod.GET,
      {
        params: {
          ...filter,
        },
      },
    ).then(convert(BaseProcedureEntity.fromJSONArray));

  fetchArchivedProceduresByOrgId = (
    orgId: string,
    page: number,
    limit: number,
    filter?: {
      name?: string;
    },
  ) =>
    TypedCloudGovResource<BaseProcedureType[], any>(this)(
      `/archived-procedures/${orgId}/organizations?page=${page}&limit=${limit}`,
      HttpMethod.GET,
      {
        params: {
          ...filter,
        },
      },
    ).then(convert(BaseProcedureEntity.fromJSONArray));
  createProcedure = (procedure: ProcedureCreate) =>
    TypedCloudGovResource<
      ProcedureType,
      ProcedureDetailType & {
        orgId?: string;
        isGrafferManaged?: boolean;
      }
    >(this)(`/procedures`, HttpMethod.POST, procedure).then(
      convert(ProcedureEntity.fromJSON),
    );
  copyProcedure = (procId: string, name: string) =>
    Resources(this)(`/procedures/${procId}/copy`, HttpMethod.POST, {
      name: name,
    }).then(convert(ProcedureEntity.fromJSON));
  exportProcedureSettings = (procedureId: string) =>
    Resources(this)(`/procedures/${procedureId}/export`, HttpMethod.GET);
  exportProcedureSettingsWithDestManageGroupId = (
    procedureId: string,
    destManageGroupId: string,
  ) =>
    Resources(this)(
      `/procedures/${procedureId}/export-with-dest-manage-group-id?destManageGroupId=${destManageGroupId}`,
      HttpMethod.GET,
    );
  exportEncryptedProcedureSettings = (procedureId: string) =>
    Resources(this)(
      `/procedures/${procedureId}/export-encrypted`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
      },
    );
  checkProcedureImportFile = (jsonData: File, orgId: string) => {
    const form = new FormData();
    form.append("orgId", orgId);
    form.append("procedureFile", jsonData);
    return Axios.default
      .post<FormData, AxiosResponse<ProcedureImportStatusType>>(
        `/files/v2/procedures-import/check`,
        form,
        setNoCache({
          headers: {
            "content-type": "multipart/form-data",
          },
        }),
      )
      .then(convert(ProcedureImportStatus.fromJSON));
  };
  checkProcedureImportFileWithDestManageGroupId = (
    jsonData: File,
    orgId: string,
  ) => {
    const form = new FormData();
    form.append("orgId", orgId);
    form.append("procedureFile", jsonData);
    return Axios.default
      .post<
        FormData,
        AxiosResponse<ProcedureImportStatusWithDestManageGroupIdType>
      >(
        `/files/v2/procedures-import/check-with-dest-manage-group-id`,
        form,
        setNoCache({
          headers: {
            "content-type": "multipart/form-data",
          },
        }),
      )
      .then(convert(ProcedureImportStatusWithDestManageGroupId.fromJSON));
  };
  checkProcedureImportSerializedFile = (zipData: File) => {
    const form = new FormData();
    form.append("procedureFile", zipData);
    return Axios.default
      .post<FormData, AxiosResponse<ProcedureImportStatusType>>(
        `/api/file-upload/procedures-import/check`,
        form,
        setNoCache({
          headers: {
            "content-type": "multipart/form-data",
          },
        }),
      )
      .then(convert(ProcedureImportStatus.fromJSON));
  };
  importProcedureFromFile = (
    jsonData: File,
    orgId: string,
    groupId: string,
    shallClearCustomPath: boolean,
  ) => {
    const form = new FormData();
    form.append("orgId", orgId);
    form.append("groupId", groupId);
    form.append("procedureFile", jsonData);
    form.append(
      "shallClearCustomPath",
      shallClearCustomPath ? "true" : "false",
    );
    return Axios.default
      .post<FormData, AxiosResponse<ProcedureType>>(
        `/files/v2/procedures-import`,
        form,
        setNoCache({
          headers: {
            "content-type": "multipart/form-data",
          },
        }),
      )
      .then(convert(ProcedureEntity.fromJSON));
  };
  importProcedureFromZipFile = (
    zipData: File,
    shallClearCustomPath: boolean,
    groupId?: string,
  ) => {
    const form = new FormData();
    if (groupId) {
      form.append("groupId", groupId);
    }
    form.append("procedureFile", zipData);
    form.append(
      "shallClearCustomPath",
      shallClearCustomPath ? "true" : "false",
    );
    return Axios.default
      .post<FormData, AxiosResponse<ProcedureType>>(
        `/api/file-upload/procedures-import`,
        form,
        setNoCache({
          headers: {
            "content-type": "multipart/form-data",
          },
        }),
      )
      .then(convert(ProcedureEntity.fromJSON));
  };
  updateProcedure = (
    id: string,
    detail: ProcedureDetailType & {
      name: string;
      isGrafferManaged?: boolean;
      publishSetting?: PublishSetting;
      summary?: string;
      detailURL?: string;
      descriptionMarkdown: string;
      updatedAt: Date;
      isExcludeFromSearch: boolean;
      isSkipArrivalMail: boolean;
      formAccessRestriction?: FormAccessRestriction;
      isLinkTopPage: boolean;
      customerDlConfig?: CustomerDlConfig;
      customPath?: string;
    },
  ) => {
    // この問題に対する一時的な対応。
    // https://github.com/graffer-inc/k8s-manifest/pull/26456#issuecomment-1480555436
    // 全ての環境で feature flag の分岐を削除できるようになれば、このコードも削除し、
    // 素直に `detail` を request body へ渡す。
    const migratedApplicantSchemaKeys = ApplicantSchemaKeysMigrator.up(
      detail.template.formSchema,
    );
    if (migratedApplicantSchemaKeys instanceof Error) {
      return Promise.reject(migratedApplicantSchemaKeys);
    }
    return TypedCloudGovResource<ProcedureType, ProcedureDetailType>(this)(
      `/procedures/${id}`,
      HttpMethod.PUT,
      {
        ...detail,
        applicantSchemaKeys: migratedApplicantSchemaKeys,
      },
    ).then(convert(ProcedureEntity.fromJSON));
  };

  updateProcedurePDFSettings = (
    id: string,
    detail: Template & {
      updatedAt: Date;
    },
  ) =>
    TypedCloudGovResource<
      ProcedureType,
      Template & {
        updatedAt: Date;
      }
    >(this)(`/procedures/${id}/pdf-settings`, HttpMethod.PUT, detail).then(
      convert(ProcedureEntity.fromJSON),
    );

  updateGrafferManaged = (
    id: string,
    detail: {
      isGrafferManaged: boolean;
      updatedAt: Date;
    },
  ) =>
    TypedCloudGovResource<
      ProcedureType,
      {
        isGrafferManaged: boolean;
        updatedAt: Date;
      }
    >(this)(`/procedures/${id}/graffer-managed`, HttpMethod.PUT, detail).then(
      convert(ProcedureEntity.fromJSON),
    );

  deleteProcedure = (procedureId: string) =>
    Resources(this)(`/procedures/${procedureId}`, HttpMethod.DELETE);

  updateOutputBasePDFBinary = (id: string, value: string, fileName: string) =>
    TypedCloudGovResource<
      ProcedureType,
      {
        baseFilePDF: string;
        baseFileName: string;
      }
    >(this)(`/procedures/${id}/output-pdf-binary`, HttpMethod.PUT, {
      baseFilePDF: value,
      baseFileName: fileName,
    }).then(convert(ProcedureEntity.fromJSON));

  fetchProcedurePresentation = (procedureId: string) =>
    Resources(this)(
      `/procedures/${procedureId}/presentation`,
      HttpMethod.GET,
      {},
    );

  downloadProcedureApplicationsCSV = (
    procedureId: string,
    dates: { dateFrom?: string; dateTo?: string },
    status: ApplyStatus[],
    applicationMetaIds: Array<string>,
    encoding: CSVEncoding,
    offset: number | undefined,
    applicationIds: Array<string>,
    option?: {
      includeTenantProcessResult: boolean | undefined;
      requireSelectableRawValue: boolean | undefined;
    },
  ) =>
    ResourcesV2(this)(
      `/procedures/${procedureId}/download-applications-csv`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
        params: {
          ...dates,
          status,
          encoding,
          offset: offset,
          applicationMetaIds,
          applicationIds,
          includeTenantProcessResult: option?.includeTenantProcessResult,
          requireSelectableRawValue: option?.requireSelectableRawValue,
        },
      },
    );

  downloadApplicationCSV = (
    applicationId: string,
    applicationMetaIds: Array<string>,
    encoding: CSVEncoding,
    option?: {
      includeTenantProcessResult?: boolean;
      requireSelectableRawValue?: boolean;
    },
  ) =>
    Resources(this)(
      `/applications/${applicationId}/output-csv`,
      // TODO use FilesResources when file sanitizer can handle hyphen appropriately. see https://graffer.atlassian.net/browse/PRODUCTS-5878
      // FilesResources(this)(
      //   `/applications/${applicationId}/application-csv`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
        params: {
          applicationMetaIds,
          encoding,
          includeTenantProcessResult: option?.includeTenantProcessResult,
          requireSelectableRawValue: option?.requireSelectableRawValue,
        },
      },
    );

  fetchAttachedFile = (fileUrl: string) =>
    FilesResources(this)(fileUrl, HttpMethod.GET, {
      responseType: "arraybuffer",
      dataType: "binary",
    });

  updateProcedurePresentation = (
    procedureId: string,
    body: ProcedurePresentationType,
  ) =>
    Resources(this)(
      `/procedures/${procedureId}/presentation`,
      HttpMethod.PUT,
      body,
    );

  addGroupToProcedure = (id: string, groupId: string) =>
    Resources(this)(`/procedures/${id}/groups`, HttpMethod.PUT, {
      groupId,
    });

  publishProcedure = (id: string) =>
    TypedCloudGovResource<ProcedureType, object>(this)(
      `/procedures/${id}/publish`,
      HttpMethod.PUT,
      {},
    ).then(convert(ProcedureEntity.fromJSON));
  returnToDraftProcedure = (id: string) =>
    TypedCloudGovResource<ProcedureType, object>(this)(
      `/procedures/${id}/return-to-draft`,
      HttpMethod.PUT,
      {},
    ).then(convert(ProcedureEntity.fromJSON));
  unPublishProcedure = (id: string) =>
    TypedCloudGovResource<ProcedureType, object>(this)(
      `/procedures/${id}/unpublish`,
      HttpMethod.PUT,
      {},
    ).then(convert(ProcedureEntity.fromJSON));
  pauseAcceptingProcedure = (id: string) =>
    TypedCloudGovResource<ProcedureType, object>(this)(
      `/procedures/${id}/pauseAccepting`,
      HttpMethod.PUT,
      {},
    ).then(convert(ProcedureEntity.fromJSON));
  resumeAcceptingProcedure = (id: string) =>
    TypedCloudGovResource<ProcedureType, object>(this)(
      `/procedures/${id}/resumeAccepting`,
      HttpMethod.PUT,
      {},
    ).then(convert(ProcedureEntity.fromJSON));

  assignCustomPath = (
    targetProcedureId: string,
    customPath: string,
    startDateime?: Date,
  ) =>
    Resources(this)(`/procedures-custom-path`, HttpMethod.POST, {
      customPath: customPath,
      targetProcedureId: targetProcedureId,
      startDatetime: startDateime,
    });

  deleteReservedCustomPath = (orgId: string, procId: string) =>
    Resources(this)(
      `/procedures-custom-path/organization/${orgId}/procedure/${procId}/reserved`,
      HttpMethod.DELETE,
      {},
    );

  updateProcedurePublishSetting = (
    id: string,
    setting: PublishSetting,
    isPublishNow: boolean,
    isStartAcceptAtPublish: boolean,
  ) =>
    TypedCloudGovResource<ProcedureType, object>(this)(
      `/procedures/${id}/publish-setting`,
      HttpMethod.PUT,
      {
        publishSetting: setting,
        isPublishNow: isPublishNow,
        isStartAcceptAtPublish: isStartAcceptAtPublish,
      },
    ).then(convert(ProcedureEntity.fromJSON));

  generateProcedurePreviewToken = (procedureId: string) =>
    TypedCloudGovResource<{ previewToken: string }, object>(this)(
      `/procedures/${procedureId}/issue-preview-token`,
      HttpMethod.POST,
      {},
    );

  downloadProcedureCSVForUpdateGroup = (orgId: string, groupIds: string[]) =>
    Resources(this)(
      groupIds.length === 0
        ? `/organizations/${orgId}/procedures/download-csv?`
        : `/organizations/${orgId}/procedures/download-csv?groupIds[]=${groupIds.join(
            "&groupIds[]=",
          )}`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
      },
    );

  bulkUpdateProcedureGroupPreview = (request: {
    data: string;
    orgId: string;
  }) =>
    TypedCloudGovResource(this)(
      `/organizations/${request.orgId}/procedures/groups/preview`,
      HttpMethod.POST,
      request,
    );

  bulkUpdateProcedureGroup = (request: { data: string; orgId: string }) =>
    TypedCloudGovResource(this)(
      `/organizations/${request.orgId}/procedures/groups`,
      HttpMethod.PUT,
      request,
    );

  // procedure mail cfg
  fetchProcedureMailCfg = (procedureId: string) =>
    Resources(this)(
      `/procedures/${procedureId}/mail-configuration`,
      HttpMethod.GET,
      {},
    );
  // procedure mail cfg (default temp)
  fetchDefaultMailCfg = () =>
    Resources(this)(
      `/procedures-config/default-mail-template`,
      HttpMethod.GET,
      {},
    );
  updateProcedureMailCfg = (procedureId: string, body: ProcedureMailCfgType) =>
    Resources(this)(
      `/procedures/${procedureId}/mail-configuration`,
      HttpMethod.PUT,
      {
        ...body,
      },
    );
  fetchProcedureQuota = (procedureId: string) =>
    TypedCloudGovResource<ProcedureQuotaEntity, object>(this)(
      `/procedures/${procedureId}/quota`,
      HttpMethod.GET,
      {},
    );
  createProcedureQuota = (id: string, quota: number) =>
    TypedCloudGovResource<ProcedureQuotaEntity, object>(this)(
      `/procedures/${id}/quota`,
      HttpMethod.POST,
      {
        quota,
      },
    );
  updateProcedureQuota = (id: string, quota: number) =>
    TypedCloudGovResource<ProcedureQuotaEntity, object>(this)(
      `/procedures/${id}/quota`,
      HttpMethod.PUT,
      {
        quota,
      },
    );
  deleteProcedureQuota = (id: string) =>
    TypedCloudGovResource(this)(
      `/procedures/${id}/quota`,
      HttpMethod.DELETE,
      {},
    );
  calcPaymentProcedure = (
    id: string,
    formData: GrafferFormData,
    items: InvoiceItemSetting[],
  ) =>
    TypedCloudGovResource<
      PaymentType,
      { formData: GrafferFormData; items: InvoiceItemSetting[] }
    >(this)(`/procedures/${id}/calc-payment`, HttpMethod.POST, {
      formData,
      items,
    });

  sendTestNotificationMail = (
    procedureId: string,
    email: string,
    template: MailTemplate,
    attributes: object,
  ) =>
    Resources(this)(
      `/procedures/${procedureId}/test-notification-mail`,
      HttpMethod.PUT,
      { email: email, template: template, mailData: attributes },
    );

  // organizations

  // 決済rawdata
  // for tenant user
  fetchPaymentRawdataTerms = () =>
    TypedCloudGovResource<string[], object>(this)(
      `/payments-rawdata/terms`,
      HttpMethod.GET,
    );
  downloadPaymentRawdata = (yyyymm: string) =>
    Resources(this)(`/payments-rawdata`, HttpMethod.GET, {
      responseType: "blob",
      dataType: "binary",
      params: {
        q: yyyymm,
      },
    });

  // for service provider
  fetchPaymentRawdataTermsByOrgId = (orgId: string) =>
    TypedCloudGovResource<string[], object>(this)(
      `/organizations/${orgId}/payments-rawdata/terms`,
      HttpMethod.GET,
    );
  downloadPaymentRawdataByOrgId = (orgId: string, yyyymm: string) =>
    Resources(this)(
      `/organizations/${orgId}/payments-rawdata`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
        params: {
          q: yyyymm,
        },
      },
    );
  downloadInternalPaymentRawdataByOrgId = (orgId: string, yyyymm: string) =>
    Resources(this)(
      `/organizations/${orgId}/internal/payments-rawdata`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
        params: {
          q: yyyymm,
        },
      },
    );

  // 3-Dセキュア認証rawdata
  // for tenant user
  fetchPayment3DSecureAuthRawdataTerms = () =>
    TypedCloudGovResource<string[], object>(this)(
      `/payments-3ds-auth-rawdata/terms`,
      HttpMethod.GET,
    );
  downloadPayment3DSecureAuthRawdata = (yyyymm: string) =>
    Resources(this)(`/payments-3ds-auth-rawdata`, HttpMethod.GET, {
      responseType: "blob",
      dataType: "binary",
      params: {
        q: yyyymm,
      },
    });

  // for service provider
  fetchPayment3DSecureAuthRawdataTermsOrgId = (orgId: string) =>
    TypedCloudGovResource<string[], object>(this)(
      `/organizations/${orgId}/payments-3ds-auth-rawdata/terms`,
      HttpMethod.GET,
    );
  downloadPayment3DSecureAuthRawdataByOrgId = (orgId: string, yyyymm: string) =>
    Resources(this)(
      `/organizations/${orgId}/payments-3ds-auth-rawdata`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
        params: {
          q: yyyymm,
        },
      },
    );
  downloadPayment3DSecureAuthInternalRawdataByOrgId = (
    orgId: string,
    yyyymm: string,
  ) =>
    Resources(this)(
      `/organizations/${orgId}/internal/payments-3ds-auth-rawdata`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
        params: {
          q: yyyymm,
        },
      },
    );

  fetchCustomerReviews = (
    page: number,
    limit: number,
    filter?: {
      from?: Date;
      to?: Date;
      procedureId?: string;
    },
  ) =>
    TypedCloudGovResource<CustomerReviewType[], any>(this)(
      `/customer-reviews?page=${page}&limit=${limit}`,
      HttpMethod.GET,
      {
        params: {
          ...filter,
        },
      },
    ).then(convert(CustomerReviewWithProcName.fromJSONArray));

  fetchCustomerReviewsByTenantId = (
    tenantId: string,
    page: number,
    limit: number,
    filter?: {
      from?: Date;
      to?: Date;
      procedureId?: string;
    },
  ) =>
    TypedCloudGovResource<CustomerReviewType[], any>(this)(
      `/customer-reviews/tenants/${tenantId}?page=${page}&limit=${limit}`,
      HttpMethod.GET,
      {
        params: {
          ...filter,
        },
      },
    ).then(convert(CustomerReviewWithProcName.fromJSONArray));

  downloadCustomerReviewsCSV = (filter?: {
    from?: Date;
    to?: Date;
    procedureId?: string;
  }) =>
    Resources(this)(`/customer-reviews/download-csv`, HttpMethod.GET, {
      responseType: "blob",
      dataType: "binary",
      params: { ...filter },
    });

  downloadCustomerReviewsCsvByTenantId = (
    tenantId: string,
    filter?: {
      from?: Date;
      to?: Date;
      procedureId?: string;
    },
  ) =>
    Resources(this)(
      `/customer-reviews/tenants/${tenantId}/download-csv`,
      HttpMethod.GET,
      {
        responseType: "blob",
        dataType: "binary",
        params: { ...filter },
      },
    );

  getUserName = (userTypeAndID: string) =>
    TypedCloudGovResource<string, any>(this)(
      `/users/${userTypeAndID}/name`,
      HttpMethod.GET,
      {},
    );

  // テナント情報を取得するAPI
  // 内部ではtenant-managementのinternal APIを呼び出している。
  // そのため、対象のテナントのみが得られる情報が取得できる。
  // 不特定のユーザーが利用する可能性のあるページでは利用しないこと。
  // ServiceProvider、OrgUser、ServiceAccountUserのみが実行できるようになっている。
  getOrganization = (orgId: string) =>
    TypedCloudGovResource<OrganizationEntity, object>(this)(
      `/private/organizations/${orgId}`,
      HttpMethod.GET,
      {},
    );

  // 製品データ削除タスク
  fetchProductDataRemovalTasks = (
    orgId: string,
    targetType: ProductDataRemovalTaskResponse["targetType"],
  ) =>
    TypedCloudGovResource<ProductDataRemovalTaskResponse[], never>(this)(
      `/product-data-removal-tasks/${orgId}/${TargetTypeApiPathParamMap[targetType]}`,
      HttpMethod.GET,
    );

  createProductDataRemovalTask = (
    orgId: string,
    targetType: ProductDataRemovalTaskResponse["targetType"],
    request: ProductDataRemovalTaskCreateRequest,
  ) =>
    TypedCloudGovResource<
      ProductDataRemovalTaskResponse,
      ProductDataRemovalTaskCreateRequest
    >(this)(
      `/product-data-removal-tasks/${orgId}/${TargetTypeApiPathParamMap[targetType]}`,
      HttpMethod.POST,
      request,
    );

  approveProductDataRemovalTask = (
    orgId: string,
    targetType: ProductDataRemovalTaskResponse["targetType"],
    id: string,
  ) =>
    TypedCloudGovResource<ProductDataRemovalTaskResponse, object>(this)(
      `/product-data-removal-tasks/${orgId}/${TargetTypeApiPathParamMap[targetType]}/${id}/approve`,
      HttpMethod.PUT,
      {},
    );

  cancelProductDataRemovalTask = (
    orgId: string,
    targetType: ProductDataRemovalTaskResponse["targetType"],
    id: string,
  ) =>
    TypedCloudGovResource<ProductDataRemovalTaskResponse, object>(this)(
      `/product-data-removal-tasks/${orgId}/${TargetTypeApiPathParamMap[targetType]}/${id}/cancel`,
      HttpMethod.PUT,
      {},
    );

  procManagement = {
    listProcs: ({
      organizationId,
      groupIds,
      statuses,
      smartApplyId,
      limit,
      page,
      sort,
    }: {
      organizationId: string;
      groupIds?: string[];
      statuses?: procManagement.StatusValue[];
      smartApplyId?: string;
      limit?: number;
      page?: number;
      sort?: {
        by: string;
        order: "desc" | "asc";
      };
    }) => {
      const param: procManagement.ProcListRequestParams = {
        params: {
          organizationId,
          groupIds: groupIds?.join(","),
          statuses: statuses?.join(","),
          smartApplyId,
          limit,
          page,
          sort: sort ? `${sort.by}:${sort.order}` : "createdAt:desc",
        },
      };
      return ProcManagementResources<
        procManagement.ProcListResponse,
        procManagement.ProcListRequestParams
      >(this)("/v1/procedures", HttpMethod.GET, param);
    },
    searchProcs: ({
      organizationId,
      groupIds,
      query,
      statuses,
      limit,
      page,
    }: {
      organizationId: string;
      groupIds?: string[];
      query: string;
      statuses?: procManagement.StatusValue[];
      limit?: number;
      page?: number;
    }) => {
      const param = {
        params: {
          organizationId,
          groupId: groupIds?.join(","),
          q: query,
          status: statuses?.join(","),
          limit,
          page,
        },
      };
      return ProcManagementResources<
        procManagement.ProcedureSearchResponse,
        procManagement.ProcedureSearchRequestParams
      >(this)("/v1/search/procedures", HttpMethod.GET, param);
    },
    find: (procId: string) => {
      return ProcManagementResources<procManagement.Procedure>(this)(
        `/v1/procedures/${procId}`,
        HttpMethod.GET,
      );
    },
    publish: (
      procId: string,
      param: procManagement.StatusUpdateRequestParams,
    ) => {
      return ProcManagementResources<
        procManagement.Procedure,
        procManagement.StatusUpdateRequestParams
      >(this)(`/v1/procedures/${procId}/publish`, HttpMethod.POST, param);
    },
    unPublish: (
      procId: string,
      param: procManagement.StatusUpdateRequestParams,
    ) => {
      return ProcManagementResources<
        procManagement.Procedure,
        procManagement.StatusUpdateRequestParams
      >(this)(`/v1/procedures/${procId}/un-publish`, HttpMethod.POST, param);
    },
    archive: (
      procId: string,
      param: procManagement.StatusUpdateRequestParams,
    ) => {
      return ProcManagementResources<
        procManagement.Procedure,
        procManagement.StatusUpdateRequestParams
      >(this)(`/v1/procedures/${procId}/archive`, HttpMethod.POST, param);
    },
    copy: (id: string, name: string) => {
      return ProcManagementResources<
        procManagement.Procedure,
        { name: string }
      >(this)(`/v1/procedures/${id}/copy`, HttpMethod.POST, { name: name });
    },
    update: (proc: procManagement.Procedure) =>
      ProcManagementResources<
        procManagement.Procedure,
        procManagement.Procedure
      >(this)(`/v1/procedures/${proc.id}`, HttpMethod.PUT, proc),
    generateProcedurePreviewToken: (proId: string, tenantId: string) => {
      return ProcManagementResources<{ previewToken: string }, object>(this)(
        `/v1/procedures/${proId}/issue-preview-token?tenantID=${tenantId}`,
        HttpMethod.POST,
        {},
      );
    },
    create: (proc: procManagement.ProcedureDetail) =>
      ProcManagementResources<
        procManagement.Procedure,
        procManagement.ProcedureDetail
      >(this)(`/v1/procedures`, HttpMethod.POST, proc),
    updateJurisdiction: (
      id: string,
      proc: procManagement.UpdateJurisdictionParams,
    ) =>
      ProcManagementResources<
        procManagement.UpdateJurisdictionParams,
        procManagement.UpdateJurisdictionParams
      >(this)(`/v1/procedures/${id}/jurisdiction`, HttpMethod.PUT, proc),
    uploadFile: (data: procManagement.FileUploadRequest) =>
      ProcManagementResources<
        procManagement.FileUploadResponse,
        procManagement.FileUploadRequest
      >(this)("/v1/files", HttpMethod.POST, data),
    uploadFileWithMultipart: (
      request: procManagement.FileStreamUploadRequest,
    ) => {
      const form = new FormData();
      form.append(
        procManagement.FORM_JSON_DATA_KEY,
        JSON.stringify(request.json),
      );
      form.append(procManagement.FORM_FILE_DATA_KEY, request.file);
      return Axios.default.post<
        FormData,
        AxiosResponse<procManagement.FileUploadResponse>
      >("/api/proc-management/v1/files", form);
    },
    listPlaces: ({
      organizationId,
      limit,
      page,
      sort,
      query,
    }: {
      organizationId: string;
      limit?: number;
      page?: number;
      sort?: {
        by: string;
        order: "desc" | "asc";
      };
      query?: string;
    }) => {
      const param: procManagement.PlaceListRequestParam = {
        params: {
          organizationId,
          limit,
          page,
          sort: sort ? `${sort.by}:${sort.order}` : "createdAt:desc",
          query,
        },
      };
      return ProcManagementResources<
        procManagement.PlaceListResponse,
        procManagement.PlaceListRequestParam
      >(this)("/v1/places", HttpMethod.GET, param);
    },
    findPlaceById: (placeId: string) => {
      return ProcManagementResources<procManagement.Place>(this)(
        `/v1/places/${placeId}`,
        HttpMethod.GET,
      );
    },
    updatePlace: (place: procManagement.Place) => {
      return ProcManagementResources<
        procManagement.Place,
        procManagement.Place
      >(this)(`/v1/places/${place.id}`, HttpMethod.PUT, place);
    },
    createPlace: (place: Omit<procManagement.Place, "id">) => {
      return ProcManagementResources<
        procManagement.Place,
        Omit<procManagement.Place, "id">
      >(this)(`/v1/places`, HttpMethod.POST, place);
    },
    listProcedureTags: ({
      organizationId,
      limit,
      page,
      sort,
      query,
    }: {
      organizationId: string;
      limit?: number;
      page?: number;
      sort?: {
        by: string;
        order: "desc" | "asc";
      };
      query?: string;
    }) => {
      const param: procManagement.ProcedureTagListRequestParam = {
        params: {
          organizationId,
          limit,
          page,
          sort: sort ? `${sort.by}:${sort.order}` : "priority:asc",
          query,
        },
      };
      return ProcManagementResources<
        procManagement.ProcedureTagListResponse,
        procManagement.ProcedureTagListRequestParam
      >(this)("/v1/procedure-tags", HttpMethod.GET, param);
    },
    createProcedureTag: (procTag: Omit<procManagement.ProcedureTag, "id">) => {
      return ProcManagementResources<
        procManagement.ProcedureTag,
        Omit<procManagement.ProcedureTag, "id">
      >(this)(`/v1/procedure-tags`, HttpMethod.POST, procTag);
    },
    deleteProcedureTag: (tagId: string) => {
      return ProcManagementResources<procManagement.ProcedureTag, any>(this)(
        `/v1/procedure-tags/${tagId}`,
        HttpMethod.DELETE,
      );
    },
    updateProcedureTag: (procTag: procManagement.ProcedureTag) => {
      return ProcManagementResources<procManagement.ProcedureTag, any>(this)(
        `/v1/procedure-tags/${procTag.id}`,
        HttpMethod.PUT,
        procTag,
      );
    },
    upsertProcManagementConfig: (
      config: procManagement.ProcManagementConfig,
      orgId: string,
    ) => {
      return ProcManagementResources<procManagement.ProcManagementConfig, any>(
        this,
      )(`/v1/configs/${orgId}`, HttpMethod.PUT, config);
    },
    findProcManagementConfig: (orgId: string) => {
      return ProcManagementResources<procManagement.ProcManagementConfig, any>(
        this,
      )(`/v1/configs/${orgId}`, HttpMethod.GET);
    },
  };
  ttzkPortal = {
    listNoticesByOrgId: (organizationId: string, page: number) => {
      const params: ttzkPortal.NoticeListRequestParam = {
        params: { organizationId: organizationId, page: page },
      };
      return TtzkPortalResources<
        ttzkPortal.NoticeListResponse,
        ttzkPortal.NoticeListRequestParam
      >(this)(`/v1/notices`, HttpMethod.GET, params);
    },
    findNotice: (noticeId: string) => {
      return TtzkPortalResources<Notice>(this)(
        `/v1/notices/${noticeId}`,
        HttpMethod.GET,
      );
    },
    createNotice: (notice: NoticeRequest) => {
      return TtzkPortalResources<Notice, NoticeRequest>(this)(
        `/v1/notices`,
        HttpMethod.POST,
        notice,
      );
    },
    updateNotice: (noticeId: string, notice: NoticeRequest) => {
      return TtzkPortalResources<Notice, NoticeRequest>(this)(
        `/v1/notices/${noticeId}`,
        HttpMethod.PUT,
        notice,
      );
    },
    deleteNotice: (noticeId: string) => {
      return TtzkPortalResources(this)(
        `/v1/notices/${noticeId}`,
        HttpMethod.DELETE,
      );
    },
    findShortcutById: (shortcutId: string) => {
      return TtzkPortalResources<ttzkPortal.Shortcut>(this)(
        `/v1/shortcuts/${shortcutId}`,
        HttpMethod.GET,
      );
    },
    listShortcutsByOrgId: (organizationId: string) => {
      const params: ttzkPortal.ShortcutListRequestParam = {
        params: { organizationId: organizationId },
      };
      return TtzkPortalResources<
        ttzkPortal.ShortcutListResponse,
        ttzkPortal.ShortcutListRequestParam
      >(this)(`/v1/shortcuts`, HttpMethod.GET, params);
    },
    createShortcut: (shortcut: ShortcutDetail) => {
      return TtzkPortalResources<Shortcut, ShortcutDetail>(this)(
        `/v1/shortcuts`,
        HttpMethod.POST,
        shortcut,
      );
    },
    updateShortcut: (shortcut: Shortcut) => {
      return TtzkPortalResources<Shortcut, Shortcut>(this)(
        `/v1/shortcuts/${shortcut.id}`,
        HttpMethod.PUT,
        shortcut,
      );
    },
    upsertPortalConfig: (config: PortalConfigRequest, orgId: string) => {
      return TtzkPortalResources<PortalConfig, any>(this)(
        `/v1/portal-configs/${orgId}`,
        HttpMethod.PUT,
        config,
      );
    },
    findPortalConfig: (orgId: string) => {
      return TtzkPortalResources<PortalConfig, any>(this)(
        `/v1/portal-configs/${orgId}`,
        HttpMethod.GET,
      );
    },
    uploadFileWithMultipart: (request: ttzkPortal.FileStreamUploadRequest) => {
      const form = new FormData();
      form.append(ttzkPortal.FORM_JSON_DATA_KEY, JSON.stringify(request.json));
      form.append(ttzkPortal.FORM_FILE_DATA_KEY, request.file);

      return TtzkPortalResources<ttzkPortal.FileUploadResponse, FormData>(this)(
        "/v1/shortcuts/images",
        HttpMethod.POST,
        form,
      );
    },
  };
}

export const setNoCache = (config: Axios.AxiosRequestConfig) => {
  if (!config.params) {
    config.params = {};
  }
  if (
    typeof URLSearchParams === "function" &&
    config.params instanceof URLSearchParams
  ) {
    config.params.append("_", Date.now().toString());
  } else {
    config.params._ = Date.now();
  }
  return config;
};

const client = Axios.default.create({
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

client.interceptors.request.use(setNoCache);

const apiClient = new CloudGovClient(client);

export default apiClient;
